import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  Folder,
  FolderOpen,
  MoreVert,
  NoteAlt,
  Description,
  DeleteOutline,
} from '@mui/icons-material';
import useNoteCategories from '../hooks/data/useNoteCategories';
import useNotes from '../hooks/data/useNotes';
import { useNoteCategoryMutations } from '../hooks/data/useNoteCategoryMutations';
import { useNoteMutations } from '../hooks/data/useNoteMutations';
import { useCategoryContext } from '../contexts/CategoryContext';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useCacheStore } from '../hooks/data/useCacheStore';
import { CACHE_UPDATED_EVENT } from '../hooks/data/useCacheFirstQuery';
import { ConfirmDialog } from './ConfirmDialog';
import { CategoryPicker } from './CategoryPicker';
import AddTemplateDialog from './AddTemplateDialog';
import { NoteCategory, Note } from '../types/notes';

const UNCATEGORIZED_NAME = 'Uncategorized';

/** Descendant category ids for disabling self+descendants in the move picker. */
function getDescendantIds(
  categoryId: string,
  categories: NoteCategory[],
): Set<string> {
  const descendants = new Set<string>();
  const queue = [categoryId];
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    for (const cat of categories) {
      if (cat.parent_id === currentId) {
        descendants.add(cat.category_id);
        queue.push(cat.category_id);
      }
    }
  }
  return descendants;
}

type Snack = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
};

interface NoteTreeProps {
  /** Called after a selection to close the mobile drawer. */
  onClose: () => void;
}

/**
 * Notes root node + note-category tree + note leaves + Trash entry.
 * Self-contained sibling of the bookmark tree; rendered inside TreeSidebar in
 * place of the old "Coming Soon" placeholder.
 */
const NoteTree: React.FC<NoteTreeProps> = ({ onClose }) => {
  const {
    selectedNoteCategory,
    selectedNote,
    noteAncestors,
    selectNoteCategory,
    selectNote,
    notesTrashOpen,
    openNotesTrash,
  } = useCategoryContext();
  const { user } = useWebAuthn();
  const { isOnline } = useOnlineStatus();

  const [expanded, setExpanded] = useState(
    !!selectedNoteCategory || !!selectedNote,
  );
  const { addCategory } = useNoteCategoryMutations();
  const [rootMenuAnchor, setRootMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<Snack>({
    open: false,
    message: '',
    severity: 'error',
  });

  // Auto-expand root + ancestor path when a note/category is active.
  const expandedAncestorIds = useMemo(() => {
    const ids = new Set<string>();
    for (const a of noteAncestors) ids.add(a.category_id);
    return ids;
  }, [noteAncestors]);

  useEffect(() => {
    if (selectedNoteCategory || selectedNote) setExpanded(true);
  }, [selectedNoteCategory, selectedNote]);

  const handleRootAddCategory = async () => {
    setRootMenuAnchor(null);
    setAddName('');
    setAddError('');
    setAddDialogOpen(true);
  };

  const handleAddSave = async () => {
    const trimmed = addName.trim();
    if (!trimmed) {
      setAddError('Category name is required.');
      return;
    }
    setAddLoading(true);
    const result = await addCategory(trimmed, null);
    setAddLoading(false);
    if (result.success) {
      setAddDialogOpen(false);
    } else {
      setAddError(result.error || 'Failed to add category.');
    }
  };

  const selectedCatId = selectedNoteCategory?.category_id ?? null;
  const selectedNoteId = selectedNote?.note_id ?? null;

  return (
    <>
      {/* Notes root */}
      <ListItemButton
        onClick={() => setExpanded((p) => !p)}
        selected={notesTrashOpen ? false : undefined}
        sx={{
          minHeight: 44,
          '& .root-menu-trigger': {
            opacity: { xs: 1, sm: 0 },
            transition: 'opacity 0.2s',
          },
          '&:hover .root-menu-trigger': { opacity: 1 },
        }}
      >
        <IconButton
          size="small"
          sx={{ minHeight: 44, minWidth: 44, mr: 0.5 }}
          aria-label={expanded ? 'Collapse Notes' : 'Expand Notes'}
          tabIndex={-1}
        >
          {expanded ? <ExpandMore /> : <ChevronRight />}
        </IconButton>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <NoteAlt />
        </ListItemIcon>
        <ListItemText
          primary="Notes"
          primaryTypographyProps={{ fontWeight: 600 }}
        />
        {user && (
          <IconButton
            size="small"
            className="root-menu-trigger"
            onClick={(e) => {
              e.stopPropagation();
              setRootMenuAnchor(e.currentTarget);
            }}
            sx={{ minHeight: 44, minWidth: 44 }}
            aria-label="Notes actions"
          >
            <MoreVert fontSize="small" />
          </IconButton>
        )}
      </ListItemButton>

      <Menu
        anchorEl={rootMenuAnchor}
        open={Boolean(rootMenuAnchor)}
        onClose={() => setRootMenuAnchor(null)}
      >
        <MenuItem onClick={handleRootAddCategory} disabled={!isOnline}>
          Add Category
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRootMenuAnchor(null);
            setTemplateDialogOpen(true);
          }}
          disabled={!isOnline}
        >
          Add Template
        </MenuItem>
      </Menu>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <NoteRootCategoryList
          onSelectCategory={(c) => {
            selectNoteCategory(c);
            onClose();
          }}
          onSelectNote={(n) => {
            selectNote(n);
            onClose();
          }}
          selectedCategoryId={selectedCatId}
          selectedNoteId={selectedNoteId}
          expandedAncestorIds={expandedAncestorIds}
          onError={(message) =>
            setSnackbar({ open: true, message, severity: 'error' })
          }
        />

        {/* Trash entry */}
        <ListItemButton
          onClick={() => {
            openNotesTrash();
            onClose();
          }}
          selected={notesTrashOpen}
          sx={{ pl: 4, minHeight: 44 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DeleteOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Trash"
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItemButton>
      </Collapse>

      {/* Add root category dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Note Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            inputProps={{ maxLength: 100 }}
            error={!!addError}
            helperText={addError}
            disabled={addLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddSave();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSave}
            variant="contained"
            disabled={addLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <AddTemplateDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

interface NoteChildrenProps {
  parentId: string | null;
  depth: number;
  onSelectCategory: (c: NoteCategory) => void;
  onSelectNote: (n: Note) => void;
  selectedCategoryId: string | null;
  selectedNoteId: string | null;
  expandedAncestorIds: Set<string>;
  onError: (message: string) => void;
}

/** Root-level note categories (only mounts when Notes root is expanded). */
const NoteRootCategoryList: React.FC<
  Omit<NoteChildrenProps, 'parentId' | 'depth'>
> = (props) => {
  const { categories, loading } = useNoteCategories(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <List component="div" disablePadding>
      {categories.map((category) => (
        <NoteCategoryTreeNode
          key={category.category_id}
          category={category}
          depth={1}
          {...props}
        />
      ))}
    </List>
  );
};

/** Lazy-loaded children (subfolders + notes) for a note category. */
const NoteCategoryChildren: React.FC<NoteChildrenProps> = ({
  parentId,
  depth,
  ...rest
}) => {
  const { categories, loading } = useNoteCategories(parentId);
  const { data: notes } = useNotes(parentId ?? undefined);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }

  return (
    <List component="div" disablePadding>
      {/* Subfolders first */}
      {categories.map((category) => (
        <NoteCategoryTreeNode
          key={category.category_id}
          category={category}
          depth={depth}
          {...rest}
        />
      ))}
      {/* Then note leaves */}
      {(notes ?? []).map((note) => (
        <NoteTreeLeaf
          key={note.note_id}
          note={note}
          depth={depth}
          selected={rest.selectedNoteId === note.note_id}
          onSelect={() => rest.onSelectNote(note)}
          onError={rest.onError}
        />
      ))}
    </List>
  );
};

interface NoteCategoryTreeNodeProps extends NoteChildrenProps {
  category: NoteCategory;
}

const NoteCategoryTreeNode: React.FC<
  Omit<NoteCategoryTreeNodeProps, 'parentId'>
> = ({
  category,
  depth,
  onSelectCategory,
  onSelectNote,
  selectedCategoryId,
  selectedNoteId,
  expandedAncestorIds,
  onError,
}) => {
  const { isOnline } = useOnlineStatus();
  const { user } = useWebAuthn();
  const cacheStore = useCacheStore();
  const { renameCategory, deleteCategory, moveCategory, addCategory } =
    useNoteCategoryMutations();
  const { moveNote, addNote } = useNoteMutations();
  const { selectNote } = useCategoryContext();

  const [expanded, setExpanded] = useState(
    expandedAncestorIds.has(category.category_id),
  );
  const [hasChildren, setHasChildren] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(category.category_name);
  const [subfolderOpen, setSubfolderOpen] = useState(false);
  const [subfolderName, setSubfolderName] = useState('');
  const [showMovePicker, setShowMovePicker] = useState(false);
  const [confirm, setConfirm] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  // Determine if this category has children (subfolders or notes) from cache.
  const recomputeHasChildren = useCallback(() => {
    const cats = cacheStore.getNoteCategories() ?? [];
    const notes = cacheStore.getNotes() ?? [];
    const childCat = cats.some((c) => c.parent_id === category.category_id);
    const childNote = notes.some(
      (n) => n.category_id === category.category_id && n.deleted_at === null,
    );
    setHasChildren(childCat || childNote);
  }, [cacheStore, category.category_id]);

  useEffect(() => {
    recomputeHasChildren();
    const handler = () => recomputeHasChildren();
    window.addEventListener(CACHE_UPDATED_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATED_EVENT, handler);
  }, [recomputeHasChildren]);

  useEffect(() => {
    if (expandedAncestorIds.has(category.category_id)) setExpanded(true);
  }, [expandedAncestorIds, category.category_id]);

  const isSelected = selectedCategoryId === category.category_id;

  const disabledMoveIds = useMemo(() => {
    const cats = cacheStore.getNoteCategories() ?? [];
    const ids = getDescendantIds(category.category_id, cats);
    ids.add(category.category_id);
    return ids;
  }, [cacheStore, category.category_id]);

  const noteCategoriesForPicker = useMemo(
    () => cacheStore.getNoteCategories() ?? [],
    [cacheStore],
  );

  const handleRenameSave = async () => {
    const result = await renameCategory(category.category_id, renameValue);
    if (result.success) {
      setRenameOpen(false);
    } else {
      onError(result.error || 'Failed to rename.');
    }
  };

  const handleSubfolderSave = async () => {
    const result = await addCategory(subfolderName, category.category_id);
    if (result.success) {
      setSubfolderOpen(false);
      setSubfolderName('');
      setExpanded(true);
    } else {
      onError(result.error || 'Failed to add subfolder.');
    }
  };

  const handleDelete = () => {
    setMenuAnchor(null);
    const cats = cacheStore.getNoteCategories() ?? [];
    const notes = cacheStore.getNotes() ?? [];
    const hasChildCats = cats.some((c) => c.parent_id === category.category_id);
    if (hasChildCats) {
      setConfirm({
        open: true,
        title: 'Cannot delete',
        message:
          'This category has subfolders. Move or delete them first, then try again.',
        onConfirm: () => setConfirm((p) => ({ ...p, open: false })),
      });
      return;
    }
    const childNotes = notes.filter(
      (n) => n.category_id === category.category_id && n.deleted_at === null,
    );
    if (childNotes.length > 0) {
      setConfirm({
        open: true,
        title: 'Delete category?',
        message: `This category has ${childNotes.length} note(s). They will be moved to "${UNCATEGORIZED_NAME}". Continue?`,
        onConfirm: async () => {
          setConfirm((p) => ({ ...p, open: false }));
          const uncategorizedId = await ensureUncategorized(
            cacheStore,
            addCategory,
          );
          for (const n of childNotes) {
            await moveNote(n.note_id, uncategorizedId);
          }
          const result = await deleteCategory(category.category_id, {
            notesReassigned: true,
          });
          if (!result.success) {
            onError(result.error || 'Failed to delete category.');
          }
        },
      });
      return;
    }
    setConfirm({
      open: true,
      title: 'Delete category?',
      message: `Delete "${category.category_name}"? This cannot be undone.`,
      onConfirm: async () => {
        setConfirm((p) => ({ ...p, open: false }));
        const result = await deleteCategory(category.category_id);
        if (!result.success) {
          onError(result.error || 'Failed to delete category.');
        }
      },
    });
  };

  const handleMoveSelect = async (newParentId: string | null) => {
    setShowMovePicker(false);
    const result = await moveCategory(category.category_id, newParentId);
    if (!result.success) {
      onError(result.error || 'Failed to move category.');
    }
  };

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelectCategory(category)}
        sx={{
          pl: 2 + depth * 2,
          minHeight: 44,
          '& .note-cat-menu': {
            opacity: { xs: 1, sm: 0 },
            transition: 'opacity 0.2s',
          },
          '&:hover .note-cat-menu': { opacity: 1 },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((p) => !p);
            }}
            sx={{ minHeight: 44, minWidth: 44, mr: 0.5 }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ExpandMore /> : <ChevronRight />}
          </IconButton>
        ) : (
          <Box sx={{ width: 44, minHeight: 44, mr: 0.5 }} />
        )}
        <ListItemIcon sx={{ minWidth: 32 }}>
          {expanded && hasChildren ? (
            <FolderOpen fontSize="small" />
          ) : (
            <Folder fontSize="small" />
          )}
        </ListItemIcon>
        <Tooltip
          title={category.category_name}
          enterDelay={500}
          placement="right"
        >
          <ListItemText
            primary={category.category_name}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </Tooltip>
        {user && (
          <IconButton
            size="small"
            className="note-cat-menu"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            sx={{ minHeight: 44, minWidth: 44 }}
            aria-label={`Actions for ${category.category_name}`}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        )}
      </ListItemButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setRenameValue(category.category_name);
            setRenameOpen(true);
          }}
          disabled={!isOnline}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setSubfolderName('');
            setSubfolderOpen(true);
          }}
          disabled={!isOnline}
        >
          Add Subfolder
        </MenuItem>
        <MenuItem
          onClick={async () => {
            setMenuAnchor(null);
            setExpanded(true);
            const result = await addNote({
              note_title: 'Untitled note',
              note_content: '',
              category_id: category.category_id,
            });
            if (result.success && result.note) {
              selectNote(result.note);
            } else {
              onError(result.error || 'Failed to add note.');
            }
          }}
          disabled={!isOnline}
        >
          Add Note
        </MenuItem>
        <MenuItem onClick={handleDelete} disabled={!isOnline}>
          Delete
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setShowMovePicker(true);
          }}
          disabled={!isOnline}
        >
          Move
        </MenuItem>
      </Menu>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <NoteCategoryChildren
          parentId={category.category_id}
          depth={depth + 1}
          onSelectCategory={onSelectCategory}
          onSelectNote={onSelectNote}
          selectedCategoryId={selectedCategoryId}
          selectedNoteId={selectedNoteId}
          expandedAncestorIds={expandedAncestorIds}
          onError={onError}
        />
      </Collapse>

      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm((p) => ({ ...p, open: false }))}
      />

      <CategoryPicker
        open={showMovePicker}
        title="Move Category"
        categories={noteCategoriesForPicker}
        disabledIds={disabledMoveIds}
        currentLocationId={category.parent_id}
        onSelect={handleMoveSelect}
        onCancel={() => setShowMovePicker(false)}
      />

      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            inputProps={{ maxLength: 100 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRenameSave();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={subfolderOpen}
        onClose={() => setSubfolderOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{`Add Subfolder to "${category.category_name}"`}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={subfolderName}
            onChange={(e) => setSubfolderName(e.target.value)}
            inputProps={{ maxLength: 100 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubfolderSave();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubfolderOpen(false)}>Cancel</Button>
          <Button onClick={handleSubfolderSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface NoteTreeLeafProps {
  note: Note;
  depth: number;
  selected: boolean;
  onSelect: () => void;
  onError: (message: string) => void;
}

const NoteTreeLeaf: React.FC<NoteTreeLeafProps> = ({
  note,
  depth,
  selected,
  onSelect,
  onError,
}) => {
  const { isOnline } = useOnlineStatus();
  const { user } = useWebAuthn();
  const cacheStore = useCacheStore();
  const { updateNote, moveNote, softDeleteNote } = useNoteMutations();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(note.note_title);
  const [showMovePicker, setShowMovePicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const noteCategoriesForPicker = useMemo(
    () => cacheStore.getNoteCategories() ?? [],
    [cacheStore],
  );

  const handleRenameSave = async () => {
    const result = await updateNote(note.note_id, { note_title: renameValue });
    if (result.success) {
      setRenameOpen(false);
    } else {
      onError(result.error || 'Failed to rename note.');
    }
  };

  const handleMoveSelect = async (newCategoryId: string | null) => {
    setShowMovePicker(false);
    const result = await moveNote(note.note_id, newCategoryId);
    if (!result.success) {
      onError(result.error || 'Failed to move note.');
    }
  };

  const handleDeleteConfirm = async () => {
    setConfirmDelete(false);
    const result = await softDeleteNote(note.note_id);
    if (!result.success) {
      onError(result.error || 'Failed to delete note.');
    }
  };

  return (
    <>
      <ListItemButton
        selected={selected}
        onClick={onSelect}
        sx={{
          pl: 2 + depth * 2,
          minHeight: 44,
          '& .note-leaf-menu': {
            opacity: { xs: 1, sm: 0 },
            transition: 'opacity 0.2s',
          },
          '&:hover .note-leaf-menu': { opacity: 1 },
        }}
      >
        <Box sx={{ width: 44, minHeight: 44, mr: 0.5 }} />
        <ListItemIcon sx={{ minWidth: 32 }}>
          <Description fontSize="small" />
        </ListItemIcon>
        <Tooltip title={note.note_title} enterDelay={500} placement="right">
          <ListItemText
            primary={note.note_title}
            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          />
        </Tooltip>
        {user && (
          <IconButton
            size="small"
            className="note-leaf-menu"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            sx={{ minHeight: 44, minWidth: 44 }}
            aria-label={`Actions for ${note.note_title}`}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        )}
      </ListItemButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setRenameValue(note.note_title);
            setRenameOpen(true);
          }}
          disabled={!isOnline}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setShowMovePicker(true);
          }}
          disabled={!isOnline}
        >
          Move
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setConfirmDelete(true);
          }}
          disabled={!isOnline}
        >
          Delete
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={confirmDelete}
        title="Move note to Trash?"
        message={`"${note.note_title}" will be moved to Trash. You can restore it later.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(false)}
      />

      <CategoryPicker
        open={showMovePicker}
        title="Move Note"
        categories={noteCategoriesForPicker}
        disabledIds={new Set()}
        currentLocationId={note.category_id}
        onSelect={handleMoveSelect}
        onCancel={() => setShowMovePicker(false)}
      />

      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            inputProps={{ maxLength: 255 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRenameSave();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Finds or creates a root-level "Uncategorized" note category and returns its id.
 */
async function ensureUncategorized(
  cacheStore: ReturnType<typeof useCacheStore>,
  addCategory: (
    name: string,
    parentId: string | null,
  ) => Promise<{ success: boolean }>,
): Promise<string | null> {
  const cats = cacheStore.getNoteCategories() ?? [];
  const existing = cats.find(
    (c) => c.parent_id === null && c.category_name === UNCATEGORIZED_NAME,
  );
  if (existing) return existing.category_id;
  await addCategory(UNCATEGORIZED_NAME, null);
  const updated = cacheStore.getNoteCategories() ?? [];
  const created = updated.find(
    (c) => c.parent_id === null && c.category_name === UNCATEGORIZED_NAME,
  );
  return created?.category_id ?? null;
}

export default NoteTree;
